import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  // These controls are installed only on the scoped ring test picker.
  #okanMode;
  #okanNeedsSize = false;
  #okanBusy = false;
  #okanFailed = false;
  #okanObserver;
  #okanListeners;
  #okanRoot;
  #okanButtons = new Map();
  #onVariantChange = (event) => this.variantChanged(event);

  get okanChoice() {
    if (this.dataset.okanTemplateView !== 'bague-choix-baguier') return null;
    return this.querySelector('.okan-ring-choice');
  }

  setupOkanChoice() {
    if (this.dataset.okanTemplateView === 'catalogue-hors-bague2') {
      this.#okanRoot = this.closest('.shopify-section') || this.parentElement;
      this.#okanObserver?.disconnect();
      if (this.#okanRoot) {
        this.#okanObserver = new MutationObserver(() => this.syncOkanPrice());
        this.#okanObserver.observe(this.#okanRoot, { childList: true, subtree: true });
      }
      this.syncOkanPrice();
      return;
    }
    const choice = this.okanChoice;
    if (!choice) return;
    this.#okanMode ??= choice.dataset.mode;
    this.#okanListeners?.abort();
    this.#okanListeners = new AbortController();
    const options = { capture: true, signal: this.#okanListeners.signal };
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const summary = event.target.closest('.okan-ring-choice__summary');
      if (this.#okanFailed && event.target instanceof HTMLInputElement && event.target.type === 'radio') {
        const input = event.target;
        setTimeout(() => {
          if (this.#okanFailed && input.isConnected && input.checked) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 0);
      }
      if (summary) {
        event.preventDefault();
        this.#okanMode = 'size';
        this.#okanNeedsSize = !this.okanChoice?.querySelector('.okan-ring-choice__size-buttons input:checked');
        this.syncOkanChoice();
      }
    }, options);
    this.#okanRoot = this.closest('.shopify-section') || this.parentElement;
    if (!this.#okanRoot) return;
    const guard = (event) => {
      if (!this.okanBlocked) return;
      const target = event.target;
      const control = target instanceof Element ? target.closest('button, input') : null;
      const form = event.type === 'submit' ? target : control?.form;
      if (!this.isOkanPurchaseForm(form)) return;
      if (event.type === 'click' && control?.type !== 'submit') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.syncOkanChoice();
    };
    this.#okanRoot.addEventListener('submit', guard, options);
    this.#okanRoot.addEventListener('click', guard, options);
    this.#okanObserver?.disconnect();
    this.#okanObserver = new MutationObserver(() => this.syncOkanChoice());
    this.#okanObserver.observe(this.#okanRoot, { childList: true, subtree: true });
    this.syncOkanChoice();
  }

  isOkanPurchaseForm(form) {
    return form instanceof HTMLFormElement && new URL(form.action, document.baseURI).pathname.endsWith('/cart/add');
  }

  get okanBlocked() {
    return !!this.okanChoice && (this.#okanNeedsSize || this.#okanBusy || this.#okanFailed);
  }

  restoreOkanButtons() {
    for (const [button, previous] of this.#okanButtons) {
      if (previous === null) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', previous);
      button.removeAttribute('data-okan-purchase-blocked');
    }
    this.#okanButtons.clear();
  }

  syncOkanPrice() {
    const row = this.querySelector('[data-okan-color-price]');
    const source = this.#okanRoot?.querySelector('product-price[data-okan-price-source]');
    if (!row || !source || source.dataset.productId !== this.dataset.productId) return;
    // Keep the native price block as the typography source and theme update target.
    // The visible amount is rendered by the same Liquid price snippet in the picker.
    const typography = getComputedStyle(source.querySelector('.price') || source);
    for (const property of ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'textTransform', 'color']) {
      row.style[property] = typography[property];
    }
    // Slightly smaller than the native price, with the same typography.
    const sourceFontSize = Number.parseFloat(typography.fontSize);
    if (Number.isFinite(sourceFontSize)) row.style.fontSize = `${sourceFontSize * 0.9}px`;
    source.hidden = true;
  }

  syncOkanChoice() {
    const choice = this.okanChoice;
    if (!choice || !this.isConnected) return;
    this.syncOkanPrice();
    this.#okanMode ??= choice.dataset.mode;
    const sizeMode = this.#okanMode === 'size';
    choice.dataset.mode = sizeMode ? 'size' : 'kit';
    const help = choice.querySelector('.okan-ring-choice__kit-help');
    if (help) help.hidden = sizeMode;
    const legend = choice.querySelector('legend');
    if (legend) {
      const typography = getComputedStyle(legend);
      choice.querySelectorAll('.okan-ring-choice__hint').forEach((hint) => {
        hint.style.fontFamily = typography.fontFamily;
        hint.style.fontSize = typography.fontSize;
        hint.style.fontWeight = typography.fontWeight;
        hint.style.letterSpacing = typography.letterSpacing;
      });
    }
    const details = choice.querySelector('details');
    if (details) details.open = sizeMode;
    const summary = choice.querySelector('summary');
    summary?.setAttribute('aria-expanded', String(sizeMode));
    if (this.#okanNeedsSize) {
      // No actual ring size (or kit) is selected in this UI state.
      choice.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });
      const selectedSize = choice.querySelector('.okan-ring-choice__selected-size');
      if (selectedSize) selectedSize.hidden = true;
    }
    const status = choice.querySelector('.okan-ring-choice__status');
    const message = this.#okanNeedsSize
      ? ''
      : this.#okanFailed
        ? 'La mise à jour a échoué. Sélectionne à nouveau une taille ou le baguier pour réessayer.'
        : '';
    if (status) {
      if (status.textContent !== message) status.textContent = message;
      status.hidden = !message;
    }
    if (!this.okanBlocked) {
      this.restoreOkanButtons();
      return;
    }
    // Preserve Atelier's own disabled state (stock/loading). Use an independent
    // accessible lock plus capture guards for clicks and keyboard form submits.
    this.#okanRoot?.querySelectorAll('button, input[type="submit"]').forEach((button) => {
      if (button.type !== 'submit' || !this.isOkanPurchaseForm(button.form)) return;
      if (!this.#okanButtons.has(button)) this.#okanButtons.set(button, button.getAttribute('aria-disabled'));
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-okan-purchase-blocked', 'true');
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onVariantChange);
    this.#okanListeners?.abort();
    this.#okanObserver?.disconnect();
    this.restoreOkanButtons();
    super.disconnectedCallback?.();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.#onVariantChange);
    this.setupOkanChoice();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const choice = this.okanChoice;
    if (choice && event.target instanceof HTMLInputElement && choice.contains(event.target)) {
      this.#okanMode = event.target.closest('.okan-ring-choice__kit') ? 'kit' : 'size';
      this.#okanNeedsSize = false;
    }
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    // Preserve the alternate ring template during Atelier's variant refresh.
    // Only the main picker on this test template receives this data attribute.
    if (['bague-choix-baguier', 'catalogue-hors-bague2'].includes(this.dataset.okanTemplateView)) {
      params.push(`view=${encodeURIComponent(this.dataset.okanTemplateView)}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const requestController = this.#abortController;
    if (this.okanChoice) {
      this.#okanBusy = true;
      this.#okanFailed = false;
      this.syncOkanChoice();
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (this.okanChoice && !response.ok) throw new Error('Variant request failed');
        return response.text();
      })
      .then((responseText) => {
        if (requestController.signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          if (this.okanChoice) throw new Error('Missing variant response');
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          this.restoreOkanButtons();

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
        this.#okanBusy = false;
        this.syncOkanChoice();
      })
      .catch((error) => {
        if (requestController.signal.aborted) return;
        if (this.okanChoice) {
          this.#okanBusy = false;
          this.#okanFailed = true;
          this.syncOkanChoice();
        }
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    let selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    if (this.#okanNeedsSize && this.okanChoice) {
      // Fetch color availability using the kit as a transport-only placeholder.
      // The UI stays unselected and purchasing stays locked until a size is chosen.
      const kit = this.okanChoice.querySelector('.okan-ring-choice__kit input');
      selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input')).filter(
        (option) => option.tagName === 'OPTION' || option.checked || option === kit
      );
    }

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
